import React, { useState, useEffect, useMemo } from "react";
import "../Overview/style.css";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "@mui/material";
import OverViewChart from "../../../components/dashboardcomponents/OverViewChart";
import DonutChart from "../../../components/dashboardcomponents/Analytics/Overview/DonutChat";
import BarChart from "../../../components/dashboardcomponents/Home/BarChart";
import TagUsage from "../../../components/dashboardcomponents/Analytics/Chats/TagUsage";
import MainHeaderAnalytics from "../../../components/dashboardcomponents/Analytics/MainHeaderAnalytics";
import { getAnalyticsTicketsStats, initAnalyticsTicketsStats, setDateRange } from "../../../store/features/getAnalyticsTicketsStats";
import { getTicketTagUsageStats, initTicketTagUsageStats } from "../../../store/features/getTicktetTagUsageStats";
import { getTicketPerformanceStats, initTicketPerformanceStats } from "../../../store/features/getTicketsPerformanceStats";
import { getTicketSourceStats, initTicketSourceStats } from "../../../store/features/getTicketsSourceStats";
import { useAnalyticsExport } from "../../../hooks/useAnalyticsExport";
import { useAnalyticsGroupId } from "../../../hooks/useAnalyticsGroupId";

function MainAnalyticsTickets() {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("New Tickets handled vs solved");
  const currentLanguage = useSelector((state) => state.language.currentLanguage);

  const {
    data: analyticsTicketsData,
    currentRange,
    dateRange,
    loading: ticketsOverviewLoading,
  } = useSelector((state) => state.analyticsTicketsStats);

  const ticketPerformanceLoading = useSelector(
    (state) => state.ticketPerformanceStats?.loading
  );
  const ticketSourceLoading = useSelector(
    (state) => state.ticketSourceStats?.loading
  );
  const ticketTagUsageLoading = useSelector(
    (state) => state.getTicketTagUsageStats?.loading
  );

  const t = useSelector((state) => state.language.translations[currentLanguage]);

  const { selectedGroupId, setSelectedGroupId } = useAnalyticsGroupId();

  const {
    exportSectionOptions,
    exportSection,
    setExportSection,
    handleExport,
    exportLoading,
  } = useAnalyticsExport("ticket", currentRange, dateRange, selectedGroupId);

  // === Time filter detection ===
  const activeTimeFilter = useMemo(() => {
    switch (currentRange) {
      case "today": return "today";
      case "yesterday": return "yesterday";
      case "7d": return "7 days";
      case "30d": return "30 days";
      case "1y": return "12 months";
      case "custom": return "custom";
      default: return "7 days";
    }
  }, [currentRange]);
  // ✅ Get tag usage stats from Redux
  const ticketTagUsageStats = useSelector((state) => state.getTicketTagUsageStats);
  const ticketPerformanceStats = useSelector((state) => state.ticketPerformanceStats);
  const ticketSourceStats = useSelector((state) => state.ticketSourceStats);

  const dataLoading = useMemo(
    () =>
      !!(
        ticketsOverviewLoading ||
        ticketPerformanceLoading ||
        ticketTagUsageLoading ||
        ticketSourceLoading
      ),
    [
      ticketsOverviewLoading,
      ticketPerformanceLoading,
      ticketTagUsageLoading,
      ticketSourceLoading,
    ]
  );

  const showTrendForRange = currentRange === '7d';
  const dataArray = [
    {
      title: "Total Tickets",
      number: analyticsTicketsData?.metrics?.totalTickets?.value || 0,
      textPercentage: showTrendForRange ? analyticsTicketsData?.metrics?.totalTickets?.change || 0 : null,
      value: t.STATS_VLW,
      isPositive: true,
      value: showTrendForRange ? 'vs last week' : undefined,
      showTrend: showTrendForRange,
    },
    {
      title: "Open Tickets",
      number: analyticsTicketsData?.metrics?.openTickets?.value || 0,
      textPercentage: showTrendForRange ? analyticsTicketsData?.metrics?.openTickets?.change || 0 : null,
      value: t.STATS_VLW,
      isPositive: true,
      value: showTrendForRange ? 'vs last week' : undefined,
      showTrend: showTrendForRange,
    },
    {
      title: "Solved Tickets",
      number: analyticsTicketsData?.metrics?.solvedTickets?.value || 0,
      textPercentage: showTrendForRange ? analyticsTicketsData?.metrics?.solvedTickets?.change || 0 : null,
      value: t.STATS_VLW,
      isPositive: false,
      value: showTrendForRange ? 'vs last week' : undefined,
      showTrend: showTrendForRange,
    },
    {
      title: "Avg 1st Response Time",
      number: analyticsTicketsData?.metrics?.avgFirstResponseTime?.value || 0,
      textPercentage: showTrendForRange ? analyticsTicketsData?.metrics?.avgFirstResponseTime?.change || 0 : null,
      value: t.STATS_VLW,
      isPositive: false,
      value: showTrendForRange ? 'vs last week' : undefined,
      showTrend: showTrendForRange,
    },
    {
      title: "1st Reply Resolution Rate",
      number: analyticsTicketsData?.metrics?.firstReplyResolutionRate?.value || 0,
      textPercentage: showTrendForRange ? analyticsTicketsData?.metrics?.firstReplyResolutionRate?.change || 0 : null,
      value: t.STATS_VLW,
      isPositive: true,
      isPercentage: true,
      value: showTrendForRange ? 'vs last week' : undefined,
      showTrend: showTrendForRange,
    },
  ];

  // ✅ Initialize when group is selected
  useEffect(() => {
    if (!selectedGroupId) return;

    Promise.all([
      dispatch(initAnalyticsTicketsStats({ group_id: selectedGroupId })),
      dispatch(initTicketTagUsageStats({ group_id: selectedGroupId })),
      dispatch(initTicketPerformanceStats({ group_id: selectedGroupId })),
      dispatch(initTicketSourceStats({ group_id: selectedGroupId })),
    ]);
  }, [dispatch, selectedGroupId]);



  const tagUsageData = ticketTagUsageStats?.tagData?.map(item => ({
    name: item.name,
    count: item.count,
    percentage: item.percentage,
    tag_id: item.tag_id
  })) || [];

  const transformedTicketData = ticketPerformanceStats?.ticketData
    ?.map(item => ({
      day: item.label,
      newTickets: item.newTickets || 0,
      solvedTickets: item.solvedTickets || 0,
      date: item.date
    })) || [];


  // Optional: If you want to always show all sources even with 0 percentage
  const donutChartData = [
    {
      name: "Chat",
      value: parseInt(ticketSourceStats.sourceData.find(s => s.source === "chat")?.percentage || "0"),
      count: ticketSourceStats.sourceData.find(s => s.source === "chat")?.count || 0,
      percentage: `${parseInt(ticketSourceStats.sourceData.find(s => s.source === "chat")?.percentage || "0")}%`,
      color: "#FA8C16"
    },
    {
      name: "Widget",
      value: parseInt(ticketSourceStats.sourceData.find(s => s.source === "widget")?.percentage || "0"),
      count: ticketSourceStats.sourceData.find(s => s.source === "widget")?.count || 0,
      percentage: `${parseInt(ticketSourceStats.sourceData.find(s => s.source === "widget")?.percentage || "0")}%`,
      color: "#148DFD"
    },
    {
      name: "Ticket Form",
      value: parseInt(ticketSourceStats.sourceData.find(s => s.source === "ticket_form")?.percentage || "0"),
      count: ticketSourceStats.sourceData.find(s => s.source === "ticket_form")?.count || 0,
      percentage: `${parseInt(ticketSourceStats.sourceData.find(s => s.source === "ticket_form")?.percentage || "0")}%`,
      color: "#52C41A"
    },

  ];


  const handleDateChange = async (newDateRange) => {
    console.log('📅 Date range changed:', newDateRange);

    // Validate dates before proceeding
    if (!newDateRange?.startDate || !newDateRange?.endDate) {
      console.error('❌ Invalid date range:', newDateRange);
      return;
    }

    // Update Redux state
    dispatch(setDateRange({
      startDate: newDateRange?.startDate,
      endDate: newDateRange?.endDate,
    }));

    await Promise.all([
      dispatch(getAnalyticsTicketsStats({
        startDate: newDateRange.startDate,
        endDate: newDateRange.endDate,
        group_id: selectedGroupId,
      })),
      dispatch(getTicketTagUsageStats({
        startDate: newDateRange.startDate,
        endDate: newDateRange.endDate,
        group_id: selectedGroupId,
      })),
      dispatch(getTicketPerformanceStats({
        startDate: newDateRange.startDate,
        endDate: newDateRange.endDate,
        group_id: selectedGroupId,
      })),
      dispatch(getTicketSourceStats({
        startDate: newDateRange.startDate,
        endDate: newDateRange.endDate,
        group_id: selectedGroupId,
      }))
    ]);
  };

  return (
    <div className="Dashboard_Main_Containerxx">
      <MainHeaderAnalytics
        title="Tickets"
        dateRange={dateRange}
        onDateChange={handleDateChange}
        exportSectionOptions={exportSectionOptions}
        exportSection={exportSection}
        onExportSectionChange={setExportSection}
        onExport={handleExport}
        exportLoading={exportLoading}
        selectedGroupId={selectedGroupId}
        onGroupChange={setSelectedGroupId}
      />

      {/* Overview Cards */}
      <OverViewChart data={dataArray} loading={dataLoading} />

      {/* Bar Chart */}
      <p style={{ margin: "32px 0" }} className="overview_main">
        {"New Tickets per Day vs Solved"}
      </p>
      <div style={{ marginBottom: "32px" }}>
        <BarChart data={transformedTicketData} activeTimeFilter={activeTimeFilter} selectedTab={activeTab} loading={dataLoading} />
      </div>

      <Box
        sx={{
          display: "flex",
          width: "100%",
          justifyContent: "space-between",
          flexDirection: { xs: "column", md: "row" },
          gap: '16px',
        }}
      >
        <TagUsage
          title="Tag Usage"
          description="Tickets per tag"
          bgcolor="#D6EDFF"
          data={tagUsageData}
          loading={ticketTagUsageStats?.loading}
        />
        <DonutChart
          title="Ticket Source"
          description="Ticket origin path"
          data={donutChartData}
          loading={ticketSourceStats?.loading}
        />
      </Box>
    </div>
  );
}

export default MainAnalyticsTickets;